/** The agent cursor's greetings: tips worded from the user's own shortcuts
 *  so a rebinding or the Windows keys read right, and the launch message. */
import {
  shortcutKeyNames,
  type ShortcutBinding,
  type ShortcutId,
} from "@/lib/shortcut-registry";
import { translate } from "@/i18n";
import { desktopGreetingMessages } from "@/i18n/messages/desktop-greeting";
import { getShortcutBinding } from "@/lib/shortcuts";
import { isShortcutDisabled } from "@/stores/shortcut-store";
import type { GreetingMessage } from "./idle-greeting-store";

export type IdleGreetingVariant =
  | "talk"
  | "hands-free"
  | "type"
  | "dictate"
  | "draw"
  | "workspace"
  | "new-chat"
  | "help";
export const IDLE_GREETING_VARIANTS: readonly IdleGreetingVariant[] = [
  "talk",
  "hands-free",
  "type",
  "dictate",
  "draw",
  "workspace",
  "new-chat",
  "help",
];

type Gesture = {
  key:
    | "gesturePress"
    | "gestureHold"
    | "gestureHoldDrag"
    | "gestureHoldDoubleTap"
    | "gestureDoubleTap";
  keys: string;
  hold?: string;
};

function keyNames(keys: string[]): string {
  return `**${shortcutKeyNames(keys)}**`;
}

function bindingGesture(binding: ShortcutBinding): Gesture | null {
  const keys = keyNames(binding.keys);
  if (binding.gesture === "chord") return { key: "gesturePress", keys };
  if (binding.gesture === "hold") return { key: "gestureHold", keys };
  if (binding.gesture === "drag") return { key: "gestureHoldDrag", keys };
  if (binding.gesture !== "doubleTap") return null;
  return binding.holdKeys?.length
    ? { key: "gestureHoldDoubleTap", keys, hold: keyNames(binding.holdKeys) }
    : { key: "gestureDoubleTap", keys };
}

/** What the user does to fire a shortcut, or null while it is off. */
function shortcutGesture(id: ShortcutId): Gesture | null {
  return isShortcutDisabled(id) ? null : bindingGesture(getShortcutBinding(id));
}

function phrase({ key, keys, hold = "" }: Gesture): string {
  return translate(desktopGreetingMessages, key, { keys, hold });
}

/** Each sentence starts with a capital, wherever the language put the
 *  lower-case gesture phrase. */
function sentenceCase(text: string): string {
  return text.replace(
    /(^|[.!?]\s+)(\p{Ll})/gu,
    (_, before: string, letter: string) => before + letter.toUpperCase(),
  );
}

type TipKey =
  | "greetType"
  | "greetDictate"
  | "greetDraw"
  | "greetWorkspace"
  | "greetNewChat";

/** Greetings that teach one shortcut, by the shortcut and its message. */
const SINGLE_SHORTCUT_TIPS: Partial<
  Record<IdleGreetingVariant, { id: ShortcutId; key: TipKey }>
> = {
  type: { id: "shortcut.input.open", key: "greetType" },
  dictate: { id: "shortcut.dictation.anywhere", key: "greetDictate" },
  draw: { id: "shortcut.draw", key: "greetDraw" },
  workspace: { id: "shortcut.toggle_workspace", key: "greetWorkspace" },
  "new-chat": { id: "shortcut.voice.new", key: "greetNewChat" },
};

/** The greeting's text, or null when its shortcut is turned off. */
export function idleGreetingText(variant: IdleGreetingVariant): string | null {
  const m = desktopGreetingMessages;
  if (variant === "help") return translate(m, "greetHelp");
  const single = SINGLE_SHORTCUT_TIPS[variant];
  if (single) {
    const gesture = shortcutGesture(single.id);
    if (!gesture) return null;
    return sentenceCase(translate(m, single.key, { gesture: phrase(gesture) }));
  }
  const handsFree = shortcutGesture("shortcut.voice.continuous");
  if (variant === "hands-free") {
    if (!handsFree) return null;
    const cancel = shortcutGesture("shortcut.voice.cancel");
    return sentenceCase(
      cancel
        ? translate(m, "greetHandsFreeCancel", {
            gesture: phrase(handsFree),
            cancel: phrase(cancel),
          })
        : translate(m, "greetHandsFree", { gesture: phrase(handsFree) }),
    );
  }
  const talk = shortcutGesture("shortcut.voice.toggle");
  if (!talk) return null;
  const talkPhrase = phrase(talk);
  if (!handsFree)
    return sentenceCase(translate(m, "greetTalk", { talk: talkPhrase }));
  if (handsFree.key === "gestureHold" && handsFree.keys === talk.keys)
    return sentenceCase(translate(m, "greetTalkHold", { talk: talkPhrase }));
  return sentenceCase(
    translate(m, "greetTalkOr", {
      talk: talkPhrase,
      listen: phrase(handsFree),
    }),
  );
}

/** What the cursor says for a greeting, or null for a tip whose shortcut is
 *  off. */
export function greetingText(message: GreetingMessage): string | null {
  if (message.kind === "tip") return idleGreetingText(message.variant);
  if (message.kind === "highlight") {
    return translate(desktopGreetingMessages, "launchHighlight", {
      text: message.text,
    });
  }
  // The welcome: the plain talk tip, else a hello.
  return (
    idleGreetingText("talk") ?? translate(desktopGreetingMessages, "greetHelp")
  );
}
